// Sidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Tooltip,
  Typography,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  SpaceDashboardOutlined as DashboardIcon,
  TuneOutlined as ConfigurationIcon,
  ViewListOutlined as ListIcon,
  GroupOutlined as GroupIcon,
  ShieldOutlined as ShieldIcon,
  CategoryOutlined as CategoryIcon,
  SubdirectoryArrowRightOutlined as SubCatIcon,
  AssignmentOutlined as AssignmentIcon,
  AssignmentTurnedInOutlined as AssignIcon,
  Inventory2Outlined as InventoryIcon,
  LocalShippingOutlined as ShipIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ApartmentOutlined as DepartmentIcon,
  BadgeOutlined as DesignationIcon,
  FolderOutlined as ProjectIcon,
  MonitorHeartOutlined as UptimeMonitoringIcon,
  LayersOutlined as ProjectTypeIcon,
  LabelOutlined as TaskTypeIcon,
  AddTaskOutlined as CreateTaskIcon,
  FlagOutlined as TaskStatusIcon,
  AccountTreeOutlined as TaskWorkflowIcon,
  TimerOutlined as TatRuleIcon,
  DynamicFormOutlined as FormIcon,
  WorkOutlineOutlined as MyWorkIcon,
  GppGoodOutlined as TaskApprovalPolicyIcon,
  RuleOutlined as MyTaskApprovalsIcon,
  CorporateFareOutlined as OrganizationIcon,
  BusinessOutlined as CompanyIcon,
  SettingsSuggestOutlined as TaskConfigIcon,
  PlaylistAddCheckOutlined as TaskExecutionIcon,
  InsightsOutlined as PerformanceIcon,
  StackedBarChartOutlined as KraKpiConfigIcon,
  AdminPanelSettingsOutlined as AdministrationIcon,
  LockPersonOutlined as AccessManagementIcon,
} from "@mui/icons-material";
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from "./layoutConstants";
import { usePermissions } from "../../context/PermissionContext";
import { ADMIN_TEAL } from "../../theme/adminTealTokens";
import sidebarWaves from "../../assets/sidebar-waves.svg";
import {
  SIDEBAR_GRADIENT,
  SIDEBAR_GOLD,
  SIDEBAR_TEXT,
  SIDEBAR_ICON,
  SIDEBAR_ICON_ACTIVE,
  SIDEBAR_ACTIVE_BG,
  SIDEBAR_ACTIVE_BG_HOVER,
  SIDEBAR_HOVER_BG,
  SIDEBAR_ITEM_MIN_HEIGHT,
} from "../../theme/sidebarBrand";

// ── Theme tokens -- gradient/gold/text/icon colors all come from
// theme/sidebarBrand.js, imported byte-for-byte identical by MainLayout/
// Sidebar.js too, so Manager, Technician and Admin render one sidebar, not
// three similar ones. ACCENT_SOFT/BORDER_COLOR below are Admin-layout-only
// (the group Chip badge, scrollbar thumb, divider) -- still derived from
// the shared gold so nothing here drifts into a second accent color. ──
const SIDEBAR_BG        = SIDEBAR_GRADIENT;
const ACCENT            = SIDEBAR_GOLD;
const ACCENT_SOFT       = alpha(SIDEBAR_GOLD, 0.18);
const BORDER_COLOR      = alpha(ADMIN_TEAL.textOnDark, 0.14);
const TEXT_PRIMARY      = SIDEBAR_TEXT;
const TEXT_MUTED        = alpha(ADMIN_TEAL.textOnDark, 0.78);

const STORAGE_KEY = "adminSidebarOpenMap";

const StyledDrawer = styled(Drawer)({
  "& .MuiDrawer-paper": {
    border: 0,
    // Very low-opacity curved-arc satin overlay (same asset as the
    // Technician/Manager sidebar) on top of the maroon gradient.
    background: `url(${sidebarWaves}) left bottom / 100% auto no-repeat, ${SIDEBAR_BG}`,
    color: TEXT_PRIMARY,
    overflowX: "hidden",
    overflowY: "auto",
    boxShadow: "8px 0 28px rgba(74, 14, 34, 0.22)",
    scrollbarWidth: "thin",
    scrollbarColor: `${alpha(ACCENT, 0.35)} transparent`,
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: alpha(ACCENT, 0.35),
      borderRadius: 8,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: alpha(ACCENT, 0.5),
    },

    position: "relative",
    // Soft light sheen behind the brand block, top-right -- same accent as
    // the Technician/Manager sidebar.
    "&::before": {
      content: '""',
      position: "absolute",
      top: -90,
      right: -70,
      width: 220,
      height: 200,
      borderRadius: "50%",
      background: "radial-gradient(closest-side, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))",
      pointerEvents: "none",
    },
  },
});

const activeStyles = (selected) => ({
  // Active treatment: a lighter translucent-white panel over the maroon
  // gradient with a 4px gold left rail -- byte-identical values to the
  // Technician/Manager sidebar (MainLayout/Sidebar.js's sidebarRowSx()).
  minHeight: SIDEBAR_ITEM_MIN_HEIGHT,
  bgcolor: selected ? SIDEBAR_ACTIVE_BG : "transparent",
  borderLeft: selected ? `4px solid ${SIDEBAR_GOLD}` : "4px solid transparent",
  "&:hover": {
    bgcolor: selected ? SIDEBAR_ACTIVE_BG_HOVER : SIDEBAR_HOVER_BG,
  },
  borderRadius: "10px",
  mx: 1,
  my: 0.25,
  pl: "18px",
  pr: "18px",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
});

// ── NavItem — a single leaf menu link ───────────────────────────────
function NavItem({ to, icon, label, selected, collapsed, onNavigate }) {
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    navigate(to);
    if (onNavigate) onNavigate();
  };

  const button = (
    <ListItemButton
      onClick={handleClick}
      sx={{ position: "relative", ...activeStyles(selected) }}
      aria-current={selected ? "page" : undefined}
    >
      <ListItemIcon
        sx={{
          color: selected ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON,
          minWidth: 38,
          marginRight: "14px",
          transition: "color 0.15s ease",
        }}
      >
        {icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: 16,
            fontWeight: selected ? 600 : 400,
            color: SIDEBAR_TEXT,
            lineHeight: 1.4,
            transition: "color 0.15s ease",
          }}
        />
      )}
    </ListItemButton>
  );

  return collapsed ? (
    <Tooltip title={label} placement="right">
      <span style={{ display: "block" }}>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

// A group is visible only when at least one permitted (visible) leaf exists
// anywhere in its subtree.
function hasVisibleLeaf(node) {
  if (node.type === "leaf") return node.visible;
  return node.children.some(hasVisibleLeaf);
}

// ── NavGroup — reusable, recursive collapsible group. Renders either a
// leaf NavItem or another nested NavGroup for each visible child, so a
// module with sub-modules (Configuration > Forms > Form Types) reuses the
// exact same component as a flat group (Category Management), instead of
// bespoke dropdown logic per module. ──────────────────────────────────
function NavGroup({ node, depth, collapsed, currentPath, openMap, onToggle, siblingIds, onNavigate }) {
  const visibleChildren = node.children.filter((child) =>
    child.type === "leaf" ? child.visible : hasVisibleLeaf(child)
  );
  if (visibleChildren.length === 0) return null;

  const isOpen = !!openMap[node.id];

  const header = (
    <ListItemButton
      onClick={() => onToggle(node.id, siblingIds)}
      sx={{ position: "relative", ...activeStyles(false) }}
    >
      <ListItemIcon sx={{ color: isOpen ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON, minWidth: 38, marginRight: "14px", transition: "color 0.15s" }}>
        {node.icon}
      </ListItemIcon>
      {!collapsed && (
        <>
          <ListItemText
            primary={node.label}
            primaryTypographyProps={{
              fontSize: 16,
              fontWeight: isOpen ? 600 : 400,
              color: SIDEBAR_TEXT,
              lineHeight: 1.4,
            }}
          />
          {node.badge ? (
            <Chip
              size="small"
              label={node.badge}
              sx={{
                ml: 1,
                height: 18,
                fontSize: 10,
                bgcolor: ACCENT_SOFT,
                color: ACCENT,
                fontWeight: 700,
                border: `1px solid ${alpha(ACCENT, 0.3)}`,
              }}
            />
          ) : null}
          {isOpen ? (
            <ExpandLessIcon sx={{ color: TEXT_MUTED, fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: TEXT_MUTED, fontSize: 18 }} />
          )}
        </>
      )}
    </ListItemButton>
  );

  const childGroupSiblingIds = node.children.filter((c) => c.type === "group").map((c) => c.id);

  return (
    <>
      {collapsed ? (
        <Tooltip title={node.label} placement="right">
          <span style={{ display: "block" }}>{header}</span>
        </Tooltip>
      ) : (
        header
      )}
      <Collapse in={isOpen && !collapsed} timeout="auto" unmountOnExit>
        <List
          sx={{
            py: 0.5,
            mx: 1,
            borderLeft: `1px solid ${BORDER_COLOR}`,
            borderRadius: "0 0 0 8px",
            pl: 0.5,
          }}
        >
          {visibleChildren.map((child) =>
            child.type === "group" ? (
              <NavGroup
                key={child.id}
                node={child}
                depth={depth + 1}
                collapsed={collapsed}
                currentPath={currentPath}
                openMap={openMap}
                onToggle={onToggle}
                siblingIds={childGroupSiblingIds}
                onNavigate={onNavigate}
              />
            ) : (
              <NavItem
                key={child.id}
                to={child.to}
                label={child.label}
                icon={child.icon}
                selected={child.matchPrefix ? currentPath.startsWith(child.to) : currentPath === child.to}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            )
          )}
        </List>
      </Collapse>
    </>
  );
}

// Walks the tree looking for the leaf matching currentPath; returns the
// ordered chain of ancestor group ids (or null if no match). Used only to
// force-open ancestors of the active route — never to close anything.
function findActiveAncestorIds(nodes, currentPath) {
  for (const node of nodes) {
    if (node.type === "leaf") {
      const matches = node.matchPrefix ? currentPath.startsWith(node.to) : currentPath === node.to;
      if (matches) return [];
    } else {
      const childResult = findActiveAncestorIds(node.children, currentPath);
      if (childResult) return [node.id, ...childResult];
    }
  }
  return null;
}

// ── Sidebar config tree — every route below is confirmed to exist in
// AppRoutes.js (see the admin sidebar restructure audit). Permission gates
// reproduce exactly what was already enforced per item before this
// restructure; nothing new is gated, nothing previously gated is loosened.
function buildSidebarTree({ can, canAny, loading }) {
  const gate = (check) => loading || check();
  let autoId = 0;
  const leaf = (label, to, icon, opts = {}) => ({
    type: "leaf",
    id: `leaf-${autoId++}`,
    label,
    to,
    icon,
    visible: opts.check ? gate(opts.check) : true,
    matchPrefix: !!opts.matchPrefix,
  });
  const group = (id, label, icon, children, badge) => ({ type: "group", id, label, icon, children, badge });

  return [
    group("configuration", "Configuration", <ConfigurationIcon fontSize="small" />, [
      group("organization", "Organization", <OrganizationIcon fontSize="small" />, [
        leaf("Unit List", "/admin/unit", <ListIcon fontSize="small" />),
        leaf("Company List", "/admin/company", <CompanyIcon fontSize="small" />),
        leaf("Departments", "/admin/department", <DepartmentIcon fontSize="small" />),
        leaf("Designations", "/admin/designation", <DesignationIcon fontSize="small" />),
      ]),
      group("projects", "Projects", <ProjectIcon fontSize="small" />, [
        leaf("Projects", "/admin/projects", <ProjectIcon fontSize="small" />, { matchPrefix: true }),
      ]),
      // Phase 44: Uptime Kuma integration -- permission-gated exactly like
      // every other leaf here (never a bare role check); Manager's
      // equivalent entry is DB-driven (sidebar_menus, see
      // database/phase-44-uptime-kuma-monitoring/003_...sql) since Manager
      // uses the separate MainLayout/Sidebar.js, not this file.
      group("uptime-monitoring", "Uptime Monitoring", <UptimeMonitoringIcon fontSize="small" />, [
        leaf("Uptime Monitoring", "/admin/uptime-monitors", <UptimeMonitoringIcon fontSize="small" />, {
          check: () => can("uptime_monitoring", "view_all"),
        }),
      ]),
      group("project-configuration", "Project Configuration", <ProjectTypeIcon fontSize="small" />, [
        leaf("Project Types", "/admin/project-types", <ProjectTypeIcon fontSize="small" />, { check: () => can("project_types", "view") }),
      ]),
      group("task-configuration", "Task Configuration", <TaskConfigIcon fontSize="small" />, [
        leaf("Task Types", "/admin/task-types", <TaskTypeIcon fontSize="small" />),
        leaf("Task Statuses", "/admin/task-statuses", <TaskStatusIcon fontSize="small" />),
        leaf("Task Workflows", "/admin/task-workflows", <TaskWorkflowIcon fontSize="small" />, { matchPrefix: true }),
        leaf("TAT Rules", "/admin/tat-rules", <TatRuleIcon fontSize="small" />),
      ]),
      // Collapsed from 8 separate "Forms" leaves into one entry point --
      // Build/Assign & Track/Review & Reports now live as tabs inside
      // FormsWorkspacePage (/admin/forms) instead of separate sidebar rows.
      // Visible if the user holds ANY forms-domain permission at all; the
      // workspace itself hides whichever of its 3 tabs the user can't use.
      leaf("Forms", "/admin/forms", <FormIcon fontSize="small" />, {
        check: () => canAny([
          { moduleKey: "form_types", actionKey: "view" },
          { moduleKey: "forms", actionKey: "view" },
          { moduleKey: "forms", actionKey: "create" },
          { moduleKey: "form_assignments", actionKey: "create" },
          { moduleKey: "form_assignments", actionKey: "view" },
          { moduleKey: "form_assignments", actionKey: "view_all" },
          { moduleKey: "form_assignments", actionKey: "manage_all" },
          { moduleKey: "assigned_forms", actionKey: "view_own" },
          { moduleKey: "form_reviews", actionKey: "view_review_queue" },
          { moduleKey: "form_results", actionKey: "view_own_result" },
          { moduleKey: "form_results", actionKey: "view_result" },
          { moduleKey: "form_execution_reports", actionKey: "view_own" },
          { moduleKey: "form_execution_reports", actionKey: "view" },
        ]),
      }),
      group("task-execution", "Task Execution", <TaskExecutionIcon fontSize="small" />, [
        // Gated on the existing `tasks` module's own permission keys
        // (database/phase-0b/001_seed_business_permissions.sql) — never a
        // role-name check, so any role (Technician/Developer/Data Analyst/
        // Manager/...) an Admin grants tasks:view/create/assign to via
        // Access Management -> Role Permissions sees exactly these items,
        // with zero React-side changes needed per role.
        leaf("My Tasks", "/admin/tasks", <AssignmentIcon fontSize="small" />, { check: () => can("tasks", "view") }),
        leaf("My Work", "/admin/my-work", <MyWorkIcon fontSize="small" />, { check: () => can("tasks", "view") }),
        leaf("Task Approval Queue", "/admin/my-task-approvals", <MyTaskApprovalsIcon fontSize="small" />),
        leaf("Assign Task", "/admin/assign-tasks", <AssignIcon fontSize="small" />, { check: () => can("tasks", "assign") }),
        leaf("Create Task", "/admin/tasks/create", <CreateTaskIcon fontSize="small" />, { check: () => can("tasks", "create") }),
      ]),
      group("approval-configuration", "Approval Configuration", <TaskApprovalPolicyIcon fontSize="small" />, [
        leaf("Task Approval Policies", "/admin/task-approval-policies", <TaskApprovalPolicyIcon fontSize="small" />),
      ]),
      group("performance-reports", "Performance & Reports", <PerformanceIcon fontSize="small" />, [
        leaf("My Performance Dashboard", "/admin/my-performance-dashboard", <MyWorkIcon fontSize="small" />),
        leaf("Team Dashboard", "/admin/team-performance-dashboard", <GroupIcon fontSize="small" />, { check: () => can("dashboards", "view_team") }),
        leaf("Project Dashboard", "/admin/project-performance-dashboard", <TaskTypeIcon fontSize="small" />, { check: () => can("dashboards", "view_project") }),
        leaf("Organization Dashboard", "/admin/organization-dashboard", <ShieldIcon fontSize="small" />, { check: () => can("dashboards", "view_organization") }),
        leaf("Performance Periods", "/admin/performance-periods", <TaskStatusIcon fontSize="small" />, { check: () => can("performance_periods", "view") }),
        leaf("Performance Metrics", "/admin/performance-metrics", <TaskStatusIcon fontSize="small" />, { check: () => can("performance_metrics", "view") }),
        leaf("Performance Profiles", "/admin/performance-profiles", <TaskStatusIcon fontSize="small" />, { check: () => can("performance_profiles", "view") }),
        leaf("KRA / KPI Configurations", "/admin/kra-kpi-configurations", <KraKpiConfigIcon fontSize="small" />, { check: () => can("kra_kpi_management", "view"), matchPrefix: true }),
        leaf("Performance Scorecards", "/admin/performance-scorecards", <TaskStatusIcon fontSize="small" />, {
          check: () => canAny([
            { moduleKey: "performance_scorecards", actionKey: "view" },
            { moduleKey: "performance_scorecards", actionKey: "view_own" },
          ]),
        }),
        leaf("Reports", "/admin/reports", <ListIcon fontSize="small" />, {
          check: () => canAny([
            { moduleKey: "reports", actionKey: "view_tasks" }, { moduleKey: "reports", actionKey: "view_task_tat" },
            { moduleKey: "reports", actionKey: "view_contributions" }, { moduleKey: "reports", actionKey: "view_forms" },
            { moduleKey: "reports", actionKey: "view_form_results" }, { moduleKey: "reports", actionKey: "view_uat" },
            { moduleKey: "reports", actionKey: "view_approvals" }, { moduleKey: "reports", actionKey: "view_workload" },
          ]),
        }),
      ]),
      group("administration", "Administration", <AdministrationIcon fontSize="small" />, [
        leaf("Roles", "/admin/roles", <ShieldIcon fontSize="small" />),
        // Existing User CRUD page (AdminStaffPage.js) at the existing route
        // /admin/users -- gated on users:view, the same permission its own
        // backend route (GET /api/users) already requires. This is
        // deliberately NOT access_management:view_users -- that permission
        // is scoped to the separate User Permission Overrides screen only
        // (see the "access-management" group below), not to viewing/managing
        // user records themselves.
        leaf("User Management", "/admin/users", <GroupIcon fontSize="small" />, { check: () => can("users", "view") }),
        leaf("SideBar List", "/admin/sidebar", <ListIcon fontSize="small" />),
      ]),
      group("access-management", "Access Management", <AccessManagementIcon fontSize="small" />, [
        leaf("Role Permissions", "/admin/access-management/roles", <ShieldIcon fontSize="small" />, { check: () => can("access_management", "view_roles") }),
        leaf("User Permissions", "/admin/access-management/users", <GroupIcon fontSize="small" />, { check: () => can("access_management", "view_users") }),
        // "Route Registry" and "Permission Audit Log" are added here as
        // their own build phases land — see PHASE_8_IMPLEMENTATION_LOG
        // (Phase 2 ships User Permissions; Phase 1 shipped Role Permissions).
      ]),
    ]),
    group("category-management", "Category Management", <CategoryIcon fontSize="small" />, [
      leaf("Category List", "/admin/category", <CategoryIcon fontSize="small" />),
      leaf("SubCategory List", "/admin/sub-category", <SubCatIcon fontSize="small" />),
    ]),
    group("inventory-management", "Inventory Management", <InventoryIcon fontSize="small" />, [
      leaf("Assets List", "/admin/inventory/products", <InventoryIcon fontSize="small" />),
      leaf("Allocation", "/admin/inventory/allocation", <ShipIcon fontSize="small" />),
    ]),
    group("account-management", "Account Management", <InventoryIcon fontSize="small" />, [
      leaf("My Reports", "/admin/accounts/my-reports", <InventoryIcon fontSize="small" />),
      leaf("Manager", "/admin/accounts/manager", <ShipIcon fontSize="small" />),
      leaf("Finance Queue", "/admin/accounts/finance", <InventoryIcon fontSize="small" />),
      leaf("Invoice Generated", "/admin/accounts/invoice-generated", <InventoryIcon fontSize="small" />),
      leaf("+ New Report", "/admin/accounts/reports/new", <ShipIcon fontSize="small" />),
    ]),
  ];
}

// ── Sidebar ────────────────────────────────────────────────────────
const Sidebar = ({ isOpen = true, onNavigate, onClose }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  // Desktop: permanent rail that collapses to icons. Below 900px: a temporary
  // drawer (never "collapsed" -- it is either open or gone), as in the
  // Manager shell.
  const isDesktop = useMediaQuery("(min-width:900px)");
  const collapsed = isDesktop ? !isOpen : false;

  const { can, canAny, loading: permissionsLoading } = usePermissions();

  const tree = useMemo(
    () => buildSidebarTree({ can, canAny, loading: permissionsLoading }),
    [can, canAny, permissionsLoading]
  );

  const [openMap, setOpenMap] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openMap));
    } catch {
      // localStorage unavailable (private mode, etc.) — in-memory state still works
    }
  }, [openMap]);

  // Auto-expand every ancestor group of the currently active route. This
  // only ever ADDS open groups — it never closes a group the user opened
  // manually, so navigating never surprises the user by collapsing an
  // unrelated section they were looking at.
  useEffect(() => {
    const ancestorIds = findActiveAncestorIds(tree, currentPath);
    if (ancestorIds && ancestorIds.length) {
      setOpenMap((prev) => {
        let changed = false;
        const next = { ...prev };
        ancestorIds.forEach((id) => {
          if (!next[id]) {
            next[id] = true;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, tree]);

  // Clicking a group header is an accordion among its direct siblings —
  // opening one collapses the others at the same level (matches the
  // original single-open-at-a-time behavior), while different nesting
  // levels remain fully independent.
  const toggleGroup = (id, siblingIds) => {
    setOpenMap((prev) => {
      const next = { ...prev };
      const willOpen = !prev[id];
      (siblingIds || []).forEach((sid) => {
        next[sid] = false;
      });
      next[id] = willOpen;
      return next;
    });
  };

  const topSiblingIds = tree.filter((n) => n.type === "group").map((n) => n.id);

  return (
    <StyledDrawer
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop ? true : isOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: { xs: 0, md: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH },
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          maxWidth: "82vw",
          transition: (t) =>
            t.transitions.create("width", {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.shorter,
            }),
        },
      }}
    >
      {/* ── Brand: same Jaipuria IT360 Desk lockup as the Manager sidebar ── */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1.3,
          px: collapsed ? 1 : 2,
          py: { xs: 1.8, sm: 2.2 },
          minHeight: { xs: 68, sm: 76 },
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "var(--radius-lg, 12px)",
            background: "#fdfaf4",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.28)",
            flexShrink: 0,
            p: "5px",
          }}
        >
          <Box
            component="img"
            src="/assets/brand/jaipuria-it360-logo-trimmed.jpg"
            alt="Jaipuria IT360 Desk"
            sx={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "6px", mixBlendMode: "multiply" }}
          />
        </Box>
        {!collapsed && (
          <Box>
            <Typography
              sx={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontStyle: "italic",
                fontWeight: 600,
                fontSize: 16,
                lineHeight: 1,
                letterSpacing: 0.2,
                color: "#fdfaf4",
              }}
            >
              Jaipuria
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(247,240,230,0.7)",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontSize: 10,
              }}
            >
              IT360 Desk
            </Typography>
          </Box>
        )}
      </Box>
      <Divider sx={{ position: "relative", zIndex: 2, borderColor: BORDER_COLOR, mb: 1.2 }} />

      {/* ── Nav ───────────────────────────────────────── */}
      <List sx={{ position: "relative", zIndex: 2, pl: 0.75, pr: 1.25, pt: 1, pb: 2, flex: 1 }}>
        <NavItem
          to="/admin/home"
          label="Dashboard"
          icon={<DashboardIcon fontSize="small" />}
          selected={currentPath === "/admin" || currentPath === "/admin/home"}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />

        {collapsed && <Divider sx={{ my: 1, borderColor: BORDER_COLOR }} />}

        {tree.map((node) => (
          <NavGroup
            key={node.id}
            node={node}
            depth={0}
            collapsed={collapsed}
            currentPath={currentPath}
            openMap={openMap}
            onToggle={toggleGroup}
            siblingIds={topSiblingIds}
            onNavigate={onNavigate}
          />
        ))}
      </List>
    </StyledDrawer>
  );
};

export default Sidebar;
